import { ArrowUpRight } from 'lucide-react'
import {
  TrendingAssetsBaseProps,
  getTypeColor,
  DEFAULT_TRENDING_TITLE,
  isAssetAmountGated,
} from './TrendingAssetsBase'
import { GatedValue } from '@/components/gate'
import { ProjectLogo } from './ProjectLogo'
import { formatCompactNumber } from '@/utils'
import { useHorizontalScroll } from '@/hooks/useHorizontalScroll'
import { InfoTooltip } from '@/components/ui/info-tooltip'

export const TrendingAssetsMobile = ({
  assets,
  onAssetClick,
  loading,
  title = DEFAULT_TRENDING_TITLE,
  financialsHidden = false,
}: TrendingAssetsBaseProps) => {
  const { scrollContainerRef, isDragging, handlers, style } = useHorizontalScroll()

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 300,
        behavior: 'smooth'
      })
    }
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 
            className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold" 
            
          >
            <span className="inline-flex items-center gap-2">
              {title}
              <InfoTooltip content="Assets currently listed on the Secondaries order book." ariaLabel={`${title} info`} />
            </span>
          </h3>
          <button
            className="w-[52px] h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#171717] flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Scroll right"
            disabled
          >
            <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
          </button>
        </div>
        <div className="h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#1c1c1c] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">Loading trending assets...</span>
        </div>
      </div>
    )
  }

  if (assets.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <h3 
            className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold" 
            
          >
            <span className="inline-flex items-center gap-2">
              {title}
              <InfoTooltip content="Assets currently listed on the Secondaries order book." ariaLabel={`${title} info`} />
            </span>
          </h3>
          <button
            className="w-[52px] h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#171717] flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Scroll right"
            disabled
          >
            <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
          </button>
        </div>
        <div className="h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#1c1c1c] px-1.5 py-1 flex items-center justify-center">
          <span className="text-sm text-muted-foreground dark:text-[rgba(253,253,253,0.72)]">No trending assets available</span>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header row: Title + Button */}
      <div className="flex items-center justify-between">
        <h3 
          className="text-[23px] leading-[32px] text-[#2d2923] dark:text-[#fdfdfd] tracking-[-0.2px] font-semibold" 
          
        >
          <span className="inline-flex items-center gap-2">
            {title}
            <InfoTooltip content="Assets currently listed on the Secondaries order book." ariaLabel={`${title} info`} />
          </span>
        </h3>
        <button
          onClick={scrollRight}
          className="w-[32px] h-[32px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#171717] hover:bg-[rgba(45,41,35,0.12)] dark:hover:bg-[#222222] flex items-center justify-center transition-colors flex-shrink-0"
          aria-label="Scroll right"
        >
          <ArrowUpRight className="w-4 h-4 text-[#2d2923] dark:text-[#fdfdfd]" />
        </button>
      </div>
      
      {/* Assets container row */}
      <div className="w-full h-[52px] rounded-full bg-[rgba(45,41,35,0.08)] dark:bg-[#1c1c1c] overflow-hidden relative">
        <div
          ref={scrollContainerRef}
          className="flex overflow-x-auto scrollbar-hide h-full items-center absolute inset-0 gap-2 pl-1 pt-1 pb-1"
          style={style}
          {...handlers}
        >
          {assets.map((asset) => (
            <div
              key={asset.order_id}
              className="bg-white dark:bg-[#0a0a0a] rounded-full flex-shrink-0 cursor-pointer hover:shadow-md dark:hover:shadow-none dark:hover:bg-[#101010] transition-shadow flex items-center gap-2.5 pl-2 pr-[18px] py-2"
              onClick={() => {
                // Prevent click if we were dragging
                if (!isDragging) {
                  onAssetClick?.(asset)
                }
              }}
            >
              <div className="flex items-start gap-2.5">
                <ProjectLogo logo={asset.logo} alt={asset.project_name} size="sm" />
                <div className="flex items-baseline gap-3">
                  <span 
                    className="text-base leading-6 text-[#2d2923] dark:text-[#fdfdfd] font-medium" 
                    
                  >
                    {asset.project_name}
                  </span>
                  <span 
                    className="font-plus-jakarta text-xs leading-4 font-semibold"
                    style={{ 
                      color: getTypeColor(asset.deal_type)
                    }}
                  >
                    {asset.deal_type}
                  </span>
                </div>
              </div>
              <div 
                className="text-sm leading-5 font-semibold text-[rgba(45,41,35,0.72)] dark:text-[rgba(253,253,253,0.72)] whitespace-nowrap" 
                
              >
                {/* Absent, not hidden: a public-listing row never carried an
                    amount. See `components/gate/GatedValue.tsx`. */}
                {isAssetAmountGated(asset, financialsHidden) ? (
                  <GatedValue label="Offered amount" widthCh={5} />
                ) : (
                  <>${formatCompactNumber(asset.offered_amount)}</>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
